import { roles } from '@transfeeradev/api-enums';
import jwtDecode from 'jwt-decode';
import { payRetailersConfig } from '@branding/payretailers/config';
import { transfeeraConfig } from '@branding/transfeera/config';

const TOKEN_NAME = 'id_token';

/**
 * @returns {LoggedUser}
 */
const get = () => {
  const token = getToken();

  if (!token) {
    return { name: '', company: {} };
  } else {
    return jwtDecode(token);
  }
};

const getToken = () => {
  return localStorage.getItem(TOKEN_NAME);
};

const set = token => {
  localStorage.setItem(TOKEN_NAME, token);
};

/**
 * @returns {LoggedUserApplication[]}
 */
const getApplications = () => {
  return get()?.allowed_applications || [];
};

/**
 * @returns {LoggedUserApplication}
 */
const getCurrentApplication = () => {
  return getApplications().find(app => get().application.id === app.id);
};

const hasApplicationAccess = applicationName => {
  return getApplications().some(app => app.name === applicationName);
};

const hasPermission = permission => {
  const permissions = getPermissions();

  if (permissions.includes('all')) {
    return true;
  }

  return permissions.includes(permission);
};

const logout = () => {
  localStorage.removeItem(TOKEN_NAME);
};

const isAdmin = () => {
  return get().profile === roles.appRoles.ADMIN.value;
};

const isAnalyst = () => {
  return get().profile === roles.appRoles.ANALYST.value;
};

const isOperator = () => {
  return get().profile === roles.appRoles.OPERATOR.value;
};

const getFeatureFlags = () => {
  return get().feature_flags || {};
};

const getPermissions = () => {
  return get().permissions || [];
};

const getBrandConfig = () => {
  return window.location.origin === process.env.VUE_APP_HOST_PAYRETAILERS ? payRetailersConfig : transfeeraConfig;
};

export default {
  get,
  set,
  isAdmin,
  isAnalyst,
  isOperator,
  getToken,
  getApplications,
  getCurrentApplication,
  hasApplicationAccess,
  hasPermission,
  logout,
  getFeatureFlags,
  getPermissions,
  getBrandConfig
};

export const permissions = Object.freeze({
  OperationPlatformAccess: 'platform_access',
  OperationAPIAccess: 'api_access',
  OperationAccountCreate: 'account_create',
  OperationUserCreate: 'user_create',
  OperationGenerateAPICredentials: 'api_credentials_generate',
  OperationPayinPix: 'payin_pix',
  OperationPayinTED: 'payin_ted',
  OperationPayinCreditCard: 'payin_credit_card',
  OperationPayinBoleto: 'payin_boleto',
  OperationPayinRefund: 'payin_refund',
  OperationPayinCancel: 'payin_cancel',
  OperationGenerateQRCode: 'qrcode_generate',
  OperationGeneratePaymentLink: 'payment_link_generate',
  OperationPayoutAutomaticPix: 'payout_automatic_pix',
  OperationPayoutPix: 'payout_pix',
  OperationPayoutTED: 'payout_ted',
  OperationPayoutBoleto: 'payout_boleto',
  OperationPayoutPixRefund: 'payout_pix_refund',
  OperationContacertaValidation: 'contacerta_validation',
  OperationBalanceWithdrawal: 'balance_withdrawal',
  OperationPixKeyRegister: 'pix_key_register',
  OperationPixKeyDelete: 'pix_key_delete',
  OperationPixKeyAcceptPortability: 'pix_key_accept_portability',
  OperationPixKeyDictQuery: 'pix_key_dict_query',
  OperationManageInfractions: 'infractions_manage'
});
